import ReactECharts from 'echarts-for-react';

/**
 * CostGapChart — "minimal editorial" variant (_2).
 *
 * Same data and chart family as CostGapChart (ECharts stacked-bar waterfall:
 * fully staffed $1.185M → savings −$1.161M → Raisy $24K). Restyled as a quiet
 * magazine infographic: opaque white card, generous whitespace, thin strokes,
 * muted slate palette with a single teal accent, small typography, no
 * gradients/glows/shadows.
 *
 * Data note: values are identical to the original (HUMAN 1,185,000;
 * RAISY 24,000; SAVINGS 1,161,000 = 98%) — nothing invented.
 */

const INK = '#334155'; // slate-700 — primary text
const MUTED = '#64748B'; // slate-500 — secondary text
const FAINT = '#94A3B8'; // slate-400 — axis labels, connector
const TEAL = '#0AB3BF'; // single brand accent

const HUMAN = 1185000;
const RAISY = 24000;
const SAVINGS = HUMAN - RAISY; // 1,161,000 (98%)

/**
 * ONE shared money formatter — used by the tooltip (hover) AND every axis tick
 * AND the bar labels, so the numbers can never disagree (RULES_BLOG_POST
 * absolute-consistency rule).
 */
const formatMoney = (v: number): string => {
  const sign = v < 0 ? '−' : '';
  const a = Math.abs(v);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a}`;
};

export const CostGapChart_2 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: '#FFFFFF',
    tooltip: {
      trigger: 'item',
      backgroundColor: '#FFFFFF',
      borderColor: '#E2E8F0',
      textStyle: { color: INK, fontSize: 12 },
      formatter: (p: { dataIndex: number }) => {
        const labels = ['Fully staffed', 'Savings (98%)', 'Raisy department'];
        const vals = [HUMAN, -SAVINGS, RAISY];
        return `${labels[p.dataIndex]}<br/><b>${formatMoney(vals[p.dataIndex])}</b>`;
      },
    },
    grid: { left: 56, right: 32, top: 40, bottom: 48 },
    xAxis: {
      type: 'category',
      data: ['Fully staffed', 'Savings (98%)', 'Raisy department'],
      axisLine: { lineStyle: { color: '#CBD5E1', width: 1 } },
      axisLabel: { color: MUTED, fontSize: 12 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'annual cost (USD)',
      nameTextStyle: { color: FAINT, fontSize: 11 },
      axisLabel: {
        color: FAINT,
        fontSize: 11,
        formatter: (v: number) => formatMoney(v),
      },
      splitLine: { lineStyle: { color: '#E2E8F0', width: 1 } },
    },
    series: [
      // invisible base keeps the running total (stacked-bar waterfall technique)
      {
        name: 'base',
        type: 'bar',
        stack: 'waterfall',
        itemStyle: { color: 'transparent', borderColor: 'transparent' },
        silent: true,
        data: [0, HUMAN, 0],
      },
      {
        name: 'value',
        type: 'bar',
        stack: 'waterfall',
        barWidth: '38%',
        data: [
          { value: HUMAN, itemStyle: { color: FAINT } },
          // negative → drawn downward from the running total (the savings drop)
          {
            value: -SAVINGS,
            itemStyle: { color: '#CBD5E1' },
            label: {
              show: true,
              position: 'inside',
              color: MUTED,
              fontWeight: 600,
              fontSize: 12,
              formatter: '98% saved',
            },
          },
          { value: RAISY, itemStyle: { color: TEAL } },
        ],
        label: {
          show: true,
          position: 'top',
          color: INK,
          fontWeight: 600,
          fontSize: 12,
          formatter: (p: { dataIndex: number }) =>
            p.dataIndex === 1 ? '' : formatMoney(p.dataIndex === 0 ? HUMAN : RAISY),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: TEAL, type: 'dashed', width: 1 },
          label: {
            color: MUTED,
            fontSize: 11,
            formatter: `you pay ${formatMoney(RAISY)}`,
          },
          data: [{ yAxis: RAISY }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 text-center">
        Figure 1. The 98% cost gap as a waterfall — fully staffed $1.185M drops by the savings to Raisy $24K. Hover for values.
      </p>
    </div>
  );
};
